export type RepeatEnd =
  | { kind: "immediate" }
  | { kind: "until"; date: string }
  | { kind: "count"; count: number }
  | { kind: "indefinite" };

export type RepeatRule =
  | { kind: "none" }
  | { kind: "interval"; unit: "minute" | "hour"; every: number; end: RepeatEnd }
  | { kind: "calendar"; unit: "day" | "week" | "month" | "year"; every: number; end: RepeatEnd };

export type RepeatOccurrencePlan = {
  key: string;
  index: number;
  date: string;
  startMinutes: number;
};

function parseLocalDateTime(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!match) return null;
  const [, year, month, day, hour, minute] = match;
  return new Date(Date.UTC(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute)));
}

function formatLocalDateTime(value: Date): string {
  const date = [value.getUTCFullYear(), String(value.getUTCMonth() + 1).padStart(2, "0"), String(value.getUTCDate()).padStart(2, "0")].join("-");
  const time = `${String(value.getUTCHours()).padStart(2, "0")}:${String(value.getUTCMinutes()).padStart(2, "0")}`;
  return `${date}T${time}`;
}

function addOccurrence(value: Date, rule: Exclude<RepeatRule, { kind: "none" }>): Date {
  const next = new Date(value);
  if (rule.kind === "interval") {
    next.setUTCMinutes(next.getUTCMinutes() + (rule.unit === "hour" ? rule.every * 60 : rule.every));
  } else if (rule.unit === "day") next.setUTCDate(next.getUTCDate() + rule.every);
  else if (rule.unit === "week") next.setUTCDate(next.getUTCDate() + rule.every * 7);
  else if (rule.unit === "month") next.setUTCMonth(next.getUTCMonth() + rule.every);
  else next.setUTCFullYear(next.getUTCFullYear() + rule.every);
  return next;
}

export function planTaskRepeat(anchor: string, rule: RepeatRule, maxOccurrences = 20): RepeatOccurrencePlan[] {
  const start = parseLocalDateTime(anchor);
  if (!start) return [];
  const toPlan = (dateTime: Date, index: number): RepeatOccurrencePlan => ({
    key: formatLocalDateTime(dateTime),
    index,
    date: formatLocalDateTime(dateTime).slice(0, 10),
    startMinutes: dateTime.getUTCHours() * 60 + dateTime.getUTCMinutes(),
  });
  if (rule.kind === "none") return [toPlan(start, 0)];

  const repeatLimit = rule.end.kind === "immediate"
    ? 0
    : rule.end.kind === "count"
      ? Math.max(0, rule.end.count)
      : maxOccurrences;
  const until = rule.end.kind === "until" ? parseLocalDateTime(rule.end.date) : null;
  const occurrences = [toPlan(start, 0)];
  let current = start;
  for (let index = 1; index <= Math.min(repeatLimit, maxOccurrences); index += 1) {
    current = addOccurrence(current, rule);
    if (until && current.getTime() > until.getTime()) break;
    occurrences.push(toPlan(current, index));
  }
  return occurrences;
}
